import { useContext, useEffect, useRef, useState } from "react";
import OwButton from "../../shared/OwButton/OwButton";
import ReactModal from "react-modal";
import ICatalog_Item from "../../../models/ICatalog_Item";
import OwTitle from "../../shared/OwTitle/OwTitle";
import { KitBuilderOption } from "./KitBuilderOption/KitBuilderOption";
import IKit_Builder_Option from "../../../models/IKit_Builder_Option";
import IKit_Builder_Data from "../../../models/IKit_Builder_Data";
import IKit_Builder_Option_ItemUI from "../../../models/IKit_Builder_Option_ItemUI";
import CatalogItemService from "../../../services/CatalogItemService";
import "./KitBuilderModal.scss";
import OwLabel from "../../shared/OwLabel/OwLabel";
import HelperService from "../../../services/HelperService";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import IOrder from "../../../models/IOrder";
import OwToggle from "../../shared/OwToggle/OwToggle";
import EndpointsEnum from "../../../models/EndpointsEnum";
import { SocketContext } from "../../../contexts/SocketContext";
import IKit_Builder_Option_Item_SelectionUI from "../../../models/IKit_Builder_Option_Item_SelectionUI";
import "./KitBuilderModal.scss";

type Props = {
    catalogItem: ICatalog_Item;
    addToCartOrOrder: (kitBuilderSelections: IKit_Builder_Data) => void;
    disableSelection: boolean;
    orderModel?: IOrder;
    /**
     * So we can track the endpoint that is being used to save the data and close the modal.
     */
    saveEndpointReturn: EndpointsEnum;
    closeModalOnAdd?: boolean;
};

export const KitBuilderModal = (props: Props) => {
    const modalElem = useRef<HTMLElement | null>(null);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);

    const socket = useContext(SocketContext)!;

    const [modalIsOpen, setModalIsOpen] = useState(false);
    // This type comes from the IAddToCartDTO
    const [kitSelections, setKitSelections] = useState<IKit_Builder_Data>({});
    const [isValid, setIsValid] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [totalPrice, setTotalPrice] = useState<number>(0);
    const [showPrice, setShowPrice] = useState(!!props.catalogItem.kit_add_each_item);

    const priceGroup = props.orderModel ? props.orderModel.Price_Group : vendorModel.Price_Group;

    const onSave = (data: any) => {
        setModalIsOpen(false);
    };

    useEffect(() => {
        modalElem.current = document.getElementById("root"); // Assuming 'root' is the id of your app's root element
        ReactModal.setAppElement(modalElem.current as HTMLElement); // Safely set the app element

        socket.on(props.saveEndpointReturn, onSave);

        return () => {
            socket.off(props.saveEndpointReturn, onSave);
        };
    }, []);

    useEffect(() => {
        const validDetails = CatalogItemService.isKitBuilderSelectionsComplete(props.catalogItem, kitSelections);

        setIsValid(validDetails.isComplete);
    }, [kitSelections]);

    const closeModal = () => {
        setModalIsOpen(false);
    };

    const showKitBuilderModal = () => {
        setModalIsOpen(true);
    };

    const updateSelection = (option: IKit_Builder_Option, itemSelections: IKit_Builder_Option_Item_SelectionUI[]) => {
        const kitSelectionKeys = Object.keys(kitSelections);

        const updatedKitSelections: IKit_Builder_Data = {
            ...kitSelections,
        };

        let foundKey = false;

        for (const kitKey of kitSelectionKeys) {
            // const kit = updatedKitSelections[kitKey];

            if (kitKey === option.name) {
                foundKey = true;

                updatedKitSelections[kitKey] = {
                    ...kitSelections[kitKey],
                };

                // @ts-expect-error
                updatedKitSelections[kitKey].selections = itemSelections
                    .filter((itemSelection) => itemSelection.selected)
                    .map((itemSelection) => {
                        return {
                            optionItem: {
                                ...itemSelection.item,
                                id: itemSelection.id,
                            },
                            subOptionSelections: itemSelection.selectedSubItem ? [itemSelection.selectedSubItem] : [],
                            qty: itemSelection.qty || 1, // Include quantity from selection
                        };
                    });
            } else {
                // add the existing kit selections to the new kit selections
                updatedKitSelections[kitKey] = kitSelections[kitKey];
            }
        }

        if (!foundKey) {
            // we need to add the last kit seleciton item.
            // @ts-expect-error
            updatedKitSelections[option.name] = {
                // @ts-expect-error
                selections: itemSelections
                    .filter((itemSelection) => itemSelection.selected)
                    .map((itemSelection) => {
                        return {
                            optionItem: {
                                ...itemSelection.item,
                                id: itemSelection.id,
                            },
                            subOptionSelections: itemSelection.selectedSubItem ? [itemSelection.selectedSubItem] : [],
                            qty: itemSelection.qty || 1, // Include quantity from selection
                        };
                    }),
            };
        }

        setKitSelections(updatedKitSelections);
    };

    useEffect(() => {
        let calculatedTotalPrice = 0;

        const kitSelectionKeys = Object.keys(kitSelections);

        const _priceGroup = props.orderModel?.Price_Group?.value || vendorModel.Price_Group?.value || "";

        if (props.catalogItem.kit_add_each_item) {
            for (const kitKey of kitSelectionKeys) {
                kitSelections[kitKey].selections.forEach((itemSelection) => {
                    const qty = itemSelection.qty || 1;
                    calculatedTotalPrice += (Number(itemSelection.optionItem.Catalog_Item?.pricing[_priceGroup]) || 0) * qty;

                    (itemSelection.subOptionSelections || []).forEach((subItemSelection) => {
                        calculatedTotalPrice += (Number(subItemSelection.Catalog_Item?.pricing[_priceGroup]) || 0) * qty;
                    });
                });
            }
        } else {
            calculatedTotalPrice = Number(props.catalogItem.pricing[_priceGroup]) || 0;
        }

        setTotalPrice(calculatedTotalPrice);
    }, [kitSelections]);

    const addKit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        props.addToCartOrOrder(kitSelections);
        setIsActive(true);

        if (props.closeModalOnAdd) {
            setModalIsOpen(false);
        }
    };

    useEffect(() => {
        if (!modalIsOpen && isActive) {
            setKitSelections({});
        }
    }, [modalIsOpen, isActive]);

    const toggleShowPrice = () => {
        setShowPrice(!showPrice);
    };

    return (
        <div className={"KitBuilderModal"}>
            <OwButton title={"build_kit"} fn={showKitBuilderModal} disabled={props.disableSelection} />

            <ReactModal
                isOpen={modalIsOpen}
                className="KitBuilderModal modal modal-dialog d-flex align-items-center justify-content-center"
                overlayClassName="modal-backdrop modal-no-backdrop-z-index">
                <div className="modal-content bg-dark">
                    {/* Fixed Header */}
                    <div className="modal-header">
                        <div className="w-100 d-flex align-items-center">
                            <OwTitle title={props.catalogItem.name} disableTranslation={true} leftIconName="fas fa-boxes" />
                            {props.catalogItem.kit_add_each_item ? (
                                <span className={"ms-2"}>
                                    <OwToggle
                                        fn={toggleShowPrice}
                                        labelOnRight={true}
                                        name={"toggle-show-price"}
                                        label={"show_price"}
                                        active={showPrice}
                                    />
                                </span>
                            ) : null}
                        </div>
                        <OwButton className="btn-danger ms-auto" centerIconClass="fas fa-times" fn={closeModal} />
                    </div>

                    {/* Scrollable Content */}
                    <div className="modal-body" style={{ maxHeight: "20rem", overflowY: "auto" }}>
                        {props.catalogItem.Kit_Builder_Options?.map((option: IKit_Builder_Option, index: number) => (
                            <KitBuilderOption
                                key={option.guid}
                                option={option}
                                updateFn={updateSelection}
                                showPrice={showPrice}
                                priceGroupName={priceGroup?.value}
                            />
                        ))}
                    </div>

                    {/* Fixed Footer */}
                    <div className="modal-footer d-flex justify-content-between">
                        <div className={"d-flex align-items-center border rounded-2 p-2"}>
                            <OwLabel title={"total"} postText={":"} className={"me-2"} isBold={true} />
                            <span className={""}>
                                {HelperService.formatCurrency({ value: totalPrice, precision: supplierModel.pricing_precision || 2 })}
                            </span>
                        </div>
                        <OwButton title="add" fn={addKit} disabled={!isValid} disableOnActive={isActive} className="btn-primary" />
                    </div>
                </div>
            </ReactModal>
        </div>
    );
};
